import './AboutHypopara.scss';

import { Container } from 'react-bootstrap';
import { faArrowUpRightFromSquare, faFilePdf, faHeart } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { MarketingSectionIntro } from '@components/marketing/MarketingSectionIntro/MarketingSectionIntro';
import { ResourceGridBlock } from '@components/marketing/ResourceGrid/ResourceGrid';
import type { ResourceGridItem } from '@components/marketing/ResourceGrid/ResourceGrid.types';

const ABOUT_RESOURCES: ResourceGridItem[] = [
  {
    slug: 'adh1-conversation',
    kind: 'PDF',
    title: 'Start the ADH1 conversation',
    description: 'A guide to help you start the conversation with your family about ADH1.',
    ctaLabel: 'Download',
    icon: faFilePdf,
  },
];

export const AboutHypopara = () => (
  <section id='about-hypopara' className='fvt-about marketing-section' aria-labelledby='fvt-about-heading'>
    <Container fluid='xxl'>
      <MarketingSectionIntro
        variant='fvt'
        eyebrow='About the condition'
        heading='What is hypoparathyroidism?'
        headingId='fvt-about-heading'
      >
        <p>
          Hypoparathyroidism (often shortened to &quot;hypoPARA&quot;) is a rare condition in which the parathyroid
          glands do not make enough parathyroid hormone. That hormone helps keep calcium balanced in your blood and
          bones, so the main things people notice are tied to low calcium.
        </p>
      </MarketingSectionIntro>

      <div className='fvt-about__grid'>
        <div className='fvt-about__body'>
          <h3>What is ADH1?</h3>
          <p>
            Your genetic test found a change, called a variant, in the calcium sensing receptor gene, <i>CASR</i>. This
            variant can cause ADH1, or autosomal dominant hypocalcemia type 1, a common form of genetic
            hypoparathyroidism that causes low blood calcium and is often passed down in familes<sup>1, 2</sup>
          </p>
          <p>
            That means some of your close blood relatives may have the same genetic change, even if they don&apos;t show
            any or similar symptoms. Genetic testing is the only way to know for sure.
          </p>
          <p>
            ADH1 is a lifelong condition. Finding out early can help relatives get answers and work with their
            healthcare provider on the care that’s right for them.
          </p>
          <div className='fvt-about__facts'>
            <div className='fvt-about__fact'>
              <div className='fvt-about__fact-label'>Inheritance pattern</div>
              <div className='fvt-about__fact-value'>
                Autosomal dominant (can be inherited from either parent)
                <div className='fvt-about__fact-caption'>
                  For <i>CASR</i>-linked hypoPARA
                </div>
              </div>
            </div>
            <div className='fvt-about__fact'>
              <div className='fvt-about__fact-label'>Eligible relatives</div>
              <div className='fvt-about__fact-value'>
                Blood Relatives
                <div className='fvt-about__fact-caption'>Parents through cousins, and more</div>
              </div>
            </div>
            <div className='fvt-about__fact'>
              <div className='fvt-about__fact-label'>Typical management</div>
              <div className='fvt-about__fact-value'>
                Calcium and vitamin D supplements
                <div className='fvt-about__fact-caption'>Endocrinologist-led care</div>
              </div>
            </div>
            <div className='fvt-about__fact'>
              <div className='fvt-about__fact-label'>Why test family</div>
              <div className='fvt-about__fact-value'>
                Early detection
                <div className='fvt-about__fact-caption'>Identify symptoms or complications</div>
              </div>
            </div>
          </div>
        </div>

        <aside className='fvt-about__association'>
          <div className='fvt-about__association-eyebrow'>
            <FontAwesomeIcon icon={faHeart} aria-hidden='true' />
            Patient advocacy partner
          </div>
          <h3 className='fvt-about__association-title'>HypoPARAthyroidism Association (HPA)</h3>
          <p>
            A patient-led, nonprofit organization working to improve the lives of people impacted by hypoparathyroidism
            through education, support, family genetic testing events, research, and advocacy.
          </p>
          <p>
            This is a good place to turn to for resources to learn more about ADH1, find a healthcare provider that
            understands hypoPARA, and connect with others in the community living with hypoPARA.
          </p>
          <div className='fvt-about__association-cta'>
            <a
              href='https://www.hypopara.org/'
              target='_blank'
              rel='noopener noreferrer'
              className='fvt-about__association-btn'
            >
              Visit hypopara.org
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} aria-hidden='true' />
            </a>
          </div>
        </aside>
      </div>

      <ResourceGridBlock
        variant='fvt'
        heading='Resources'
        lead='Things you can read now, or download and share with your family.'
        items={ABOUT_RESOURCES}
        links={{
          'adh1-conversation': 'https://drive.google.com/file/d/1FbX4z4F8DtvW6BJIQQMaxSp3NT3iTtE9/view?usp=sharing',
        }}
      />
    </Container>
  </section>
);
